<script setup lang="ts">
import { OptionMain } from '~/lib/services/store/catalog/catalog.dtos'
import { OptionsGroupsType } from '~/lib/services/store/common.dto'

const props = defineProps<{
  option: OptionMain
  minGroupAmount: number
}>()

const emit = defineEmits<{
  (e: 'select-option', option: OptionMain): void
}>()

const { format } = useFormatAmount()
const { translateInterpolated } = useTranslation()

const isOptionSelected = computed(() => props.option.qty === 1)
const customizationString = computed(() => {
  if (!isOptionSelected.value) return ''
  return translateInterpolated(getProductCustomizationResume(props.option.optionsGroups || []))
})
const optionSelected3LevelChoiceText = computed(() => {
  if (!isOptionSelected.value) return ''
  const any3LevelMandatoryGroup = props.option.optionsGroups.find(
    optG => optG.type === OptionsGroupsType.CHOICE
  )
  if (!any3LevelMandatoryGroup || !any3LevelMandatoryGroup.options.some(opt => opt.qty === 1))
    return ''
  const selected3LevelMandatoryOption = any3LevelMandatoryGroup.options.find(opt => opt.qty === 1)
  if (selected3LevelMandatoryOption)
    return `, ${any3LevelMandatoryGroup.title} ${selected3LevelMandatoryOption.name}`
  return ''
})

const selectOption = () => {
  emit('select-option', props.option)
}
</script>

<template>
  <div
    data-test="choice-option"
    class="flex cursor-pointer flex-row items-center gap-2 p-2"
    :class="{ 'border-l-4 border-l-mcd-main bg-mcd-secondary': isOptionSelected }"
    @click="selectOption"
  >
    <div class="relative h-12 w-12 min-w-[3rem] rounded-[50%] bg-mcd-secondary">
      <ImageResolver
        class="absolute left-1/2 top-1/2 h-9 w-auto -translate-x-1/2 -translate-y-1/2"
        :url="option.imageUrl"
        :alt="option.name"
      />
    </div>

    <div class="w-full">
      <p class="line-clamp-2 flex-1 text-sm font-bold">
        {{ option.name }} {{ optionSelected3LevelChoiceText }}
      </p>
      <p
        v-if="isOptionSelected && customizationString"
        v-dompurify-html="customizationString"
        class="mt-1 line-clamp-1 text-xs text-mcd-secondaryDarkGrey"
      ></p>
    </div>
    <p class="min-w-[60px] text-sm text-mcd-secondaryDarkGrey">
      <template v-if="option.price.amount > minGroupAmount">
        +{{ format(option.price.amount - minGroupAmount) }}
      </template>
    </p>
  </div>
</template>
